import React, { useState, useEffect } from 'react';
import {
  Button,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  Box,
  Link,
  FormControlLabel,
  Checkbox,
  FormHelperText,
  Tooltip,
  IconButton,
} from '@mui/material';
import { useBreakpointDown } from '@/hooks/use-breakpoint';
import { useForm, Controller, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslations } from '@/i18n/use-translations';
import i18n, { supportedLocales, type SupportedLocales } from '@/i18n/i18n';
import type { TranslationKey } from '@/i18n/translation-key';
import type { z } from 'zod';
import { userOnboardingSchema } from '@/schemas/user/onboarding-schema';
import Modal from '@/components/common/modal';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { ClerkProfilePhotoEditor } from '@/components/nav-bar/clerk-profile-photo-editor';
type FormData = z.infer<typeof userOnboardingSchema>;

interface UserDataFormProps {
  onComplete: (data: FormData) => void;
  defaultValues?: FormData;
}

const UserDataForm: React.FC<UserDataFormProps> = ({ onComplete, defaultValues }) => {
  const t = useTranslations();
  const [open, setOpen] = useState(true);
  const isMobile = useBreakpointDown('sm');

  const cookiePolicyLang: SupportedLocales = i18n.language?.startsWith('de') ? 'de' : 'en';
  const cookiePolicyHref = `/legal/cookies?lang=${cookiePolicyLang}`;

  const formDefaultValues = defaultValues ?? {
    firstName: '',
    lastName: '',
    language: 'en' as const,
    essentialCookiesAccepted: false,
  };

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isDirty },
  } = useForm<FormData>({
    mode: 'onChange',
    resolver: zodResolver(userOnboardingSchema),
    defaultValues: formDefaultValues,
  });

  useEffect(() => {
    if (!defaultValues) return;
    if (isDirty) return;
    reset(defaultValues);
    if (defaultValues.language) {
      void i18n.changeLanguage(defaultValues.language);
    }
  }, [defaultValues, isDirty, reset]);

  const watched = useWatch({ control });
  const canContinue = userOnboardingSchema.safeParse(watched ?? formDefaultValues).success;

  const onSubmit = (formData: FormData) => {
    setOpen(false);
    onComplete(formData);
  };

  return (
    <Modal
      open={open}
      handleClose={() => {}}
      hideCloseIcon
      title={t('onBoarding.welcome')}
      description={t('onBoarding.personalize')}
      size="md"
      fullScreen={isMobile}
      width={isMobile ? undefined : '450px'}
      height={isMobile ? undefined : '640px'}
      footer={
        <Button variant="contained" color="info" type="submit" form="user-data-form" disabled={!canContinue}>
          {t('onBoarding.continue')}
        </Button>
      }
    >
      <Box
        component="form"
        id="user-data-form"
        onSubmit={handleSubmit(onSubmit)}
        noValidate
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          height: isMobile ? '100%' : 'auto',
          padding: isMobile ? 2 : 0,
          overflowY: isMobile ? 'auto' : 'visible',
        }}
      >
        <Stack direction="row" alignItems="center" spacing={2}>
          <ClerkProfilePhotoEditor avatarSize={72} />
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="subtitle2">{t('user.profile.userInfo.profilePhoto')}</Typography>
            <Typography variant="caption" color="text.secondary" display="block">
              {t('user.profile.userInfo.profilePhotoHint')}
            </Typography>
          </Box>
        </Stack>

        <Controller
          name="firstName"
          control={control}
          render={({ field }) => (
            <Box>
              <Typography variant="subtitle2">{t('onBoarding.firstName')}*</Typography>
              <TextField
                {...field}
                fullWidth
                variant="standard"
                placeholder={t('onBoarding.firstNamePlaceholder')}
                error={!!errors.firstName}
                helperText={errors.firstName?.message}
              />
            </Box>
          )}
        />

        <Controller
          name="lastName"
          control={control}
          render={({ field }) => (
            <Box>
              <Typography variant="subtitle2">{t('onBoarding.lastName')}*</Typography>
              <TextField
                {...field}
                fullWidth
                variant="standard"
                placeholder={t('onBoarding.lastNamePlaceholder')}
                error={!!errors.lastName}
                helperText={errors.lastName?.message}
              />
            </Box>
          )}
        />

        <Controller
          name="language"
          control={control}
          render={({ field }) => (
            <FormControl variant="standard" fullWidth error={!!errors.language}>
              <InputLabel>{t('onBoarding.language')}</InputLabel>
              <Select
                {...field}
                value={field.value || supportedLocales[0]}
                onChange={(e) => {
                  field.onChange(e.target.value as SupportedLocales);
                  i18n.changeLanguage(e.target.value);
                }}
              >
                {supportedLocales.map((locale) => (
                  <MenuItem key={locale} value={locale}>
                    {t(`languages.${locale}` as TranslationKey)}
                  </MenuItem>
                ))}
              </Select>
              <Typography variant="caption">{t('onBoarding.languageHint')}</Typography>
            </FormControl>
          )}
        />

        <Stack spacing={1}>
          <Typography variant="subtitle1">{t('onBoarding.cookiePrompt')}</Typography>
          <Typography variant="body2" color="text.secondary">
            {t('onBoarding.cookiePolicyHint')}{' '}
            <Link href={cookiePolicyHref} target="_blank" rel="noopener noreferrer" underline="hover">
              {t('onBoarding.cookiePolicyLink')}
            </Link>
            {t('onBoarding.cookiePolicyOpensNewTab')}
          </Typography>

          <Controller
            name="essentialCookiesAccepted"
            control={control}
            render={({ field }) => (
              <FormControl required error={!!errors.essentialCookiesAccepted} component="fieldset" variant="standard">
                <Stack direction="row" alignItems="flex-start" spacing={0.5}>
                  <FormControlLabel
                    sx={{ alignItems: 'flex-start', mr: 0, flex: 1 }}
                    control={
                      <Checkbox
                        checked={field.value}
                        onChange={(_, checked) => field.onChange(checked)}
                        color="primary"
                      />
                    }
                    label={
                      <Typography variant="body2" sx={{ pt: 0.5 }}>
                        {t('onBoarding.essentialCookiesLabel')}
                      </Typography>
                    }
                  />
                  <Tooltip title={t('onBoarding.essentialCookiesTooltip')} placement="top" enterTouchDelay={0}>
                    <IconButton size="small" aria-label={t('onBoarding.essentialCookiesInfoAria')} sx={{ mt: 0.25 }}>
                      <InfoOutlinedIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Stack>
                <FormHelperText>
                  {errors.essentialCookiesAccepted?.message === 'essential_cookies_required'
                    ? t('onBoarding.essentialCookiesRequired')
                    : errors.essentialCookiesAccepted?.message}
                </FormHelperText>
              </FormControl>
            )}
          />
        </Stack>
      </Box>
    </Modal>
  );
};

export default UserDataForm;
